import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { UnitType } from '@/documents/types/document';

/**
 * GET /api/Master/GetUnitOfMeasurements?query={q}&PageNumber=1&PageSize=50
 *
 * Master-data typeahead for measurement units. Used by the Labs
 * document sub-form (PROD-797 PR 3) to populate
 * `patientDocumentLabs[].unitType`.
 *
 * Wire-shape dev-curl verified 2026-05-15 by Andrew:
 * ```json
 * [
 *   {
 *     "Id": "2d3ff48b-...",
 *     "Code": "%/100{WBC}",
 *     "Display": "percent / 100 WBC",
 *     "System": "https://www.hl7.org/fhir/valueset-ucum-units.html"
 *   },
 *   ...
 * ]
 * ```
 *
 * Wire-shape GOTCHA — capital-letter inconsistency:
 *   The master endpoint returns `{ Id, Code, Display, System }` with
 *   CAPITAL-letter keys. BUT the document body's `patientDocumentLabs[].unitType`
 *   field uses LOWERCASE keys: `{ display, code }`.
 *
 *   This is an asymmetric .NET serializer config on the backend — the
 *   master tables emit PascalCase while the document blob uses
 *   camelCase. Consumers translate via `unitOfMeasurementToUnitType()`
 *   exported below.
 *
 *   Compile-time enforcement (Samer review PR #139 §1):
 *     `UnitType` in `document.ts` requires non-null `code` and `display`,
 *     so a direct `unitType: row` pass-through fails to compile —
 *     `UnitOfMeasurementRow` has neither key (PascalCase). The helper
 *     remains the only sanctioned path from master shape to wire shape.
 *
 * @see {@link ../../../docs/CONVERSION_NOTES_PROD-797.md} (PR 3 wire-shape gotcha table)
 *
 * Paging: backend supports `PageNumber` + `PageSize`; we default to
 * 50 in line with Flutter. The popup picker doesn't expose paging UI
 * — V1 ships the first 50 matches and the user refines with a more
 * specific query.
 *
 * Empty / very-short queries: short-circuit to avoid pulling the full
 * UCUM unit table on mount (it's thousands of entries).
 */

export interface UnitOfMeasurementRow {
  Id: string;
  Code: string;
  Display: string;
  System?: string;
}

interface UseUnitOfMeasurementsParams {
  query: string;
  pageNumber?: number;
  pageSize?: number;
  enabled?: boolean;
}

export function useUnitOfMeasurements(params: UseUnitOfMeasurementsParams) {
  const { query, pageNumber = 1, pageSize = 50, enabled = true } = params;
  const trimmed = query.trim();
  return useQuery<UnitOfMeasurementRow[]>({
    queryKey: ['unit-of-measurements', trimmed, pageNumber, pageSize],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({
        query: trimmed,
        PageNumber: String(pageNumber),
        PageSize: String(pageSize),
      });
      return get<UnitOfMeasurementRow[]>(
        `/api/Master/GetUnitOfMeasurements?${qs.toString()}`,
        { signal },
      );
    },
    enabled: enabled && trimmed.length > 0,
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Translate a master-table `UnitOfMeasurementRow` (PascalCase wire) to
 * the document body's `UnitType` shape (camelCase). See the GOTCHA
 * docblock above — the backend uses different casing for the two
 * surfaces and TypeScript can't catch the drift without this helper.
 */
export function unitOfMeasurementToUnitType(row: UnitOfMeasurementRow): UnitType {
  return {
    code: row.Code,
    display: row.Display,
  };
}
